import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { CloseIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import FormField from "@/components/ui/formField";
import { FeatureDetail } from "@/types/features";
import {
  AddGroupFormData,
  GroupStatus,
  UserGroupPermissionPayload
} from "@/types/groups";

import { getPermissionOptions, GROUP_STATUS_OPTIONS } from "../data";
import FormFooterActions from "./FormFooterAction";
import PermissionTable from "./PermissionTable";

const TEST_ID_PREFIX = "add-group-panel";

type AddGroupPanelProps = {
  featureList?: FeatureDetail[]; // List of features to be used in the permission table
  onClose?: () => void; // Optional onClose function for future use
  onSubmit: (data: AddGroupFormData) => void;
  isSubmitting: boolean;
};

export const AddGroupPanel = ({
  featureList = [],
  onClose,
  onSubmit,
  isSubmitting = false
}: AddGroupPanelProps) => {
  const getUserGroupPermission = featureList?.map((item) => ({
    name: item.name,
    feature_id: item.id,
    selectedPermission: getPermissionOptions(item.name)[0]
  })) as UserGroupPermissionPayload[];
  const addGroupFormProviderProps = useForm<AddGroupFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      status: "" as GroupStatus,
      group_permission: getUserGroupPermission
    }
  });
  const { register, formState, control, handleSubmit, setValue, resetField } =
    addGroupFormProviderProps;

  useEffect(() => {
    // To show a confirmation dialog when the user tries to leave the page
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <FormProvider {...addGroupFormProviderProps}>
      <form
        className="flex flex-1 flex-col"
        onSubmit={handleSubmit(onSubmit)}
        name="Add New Group"
      >
        <div className="sticky top-0 z-10 p-6 bg-white">
          <div className="flex flex-row justify-between items-center">
            <p className="text-txtHeading font-sans font-bold text-[20px]">
              Add New Group
            </p>
            <div className="flex gap-3">
              <button
                type="button" // Default button type is submit. Thus, need to set type="button" to prevent form submission
                aria-label="Close Add Group Panel"
                onClick={onClose}
                className="bg-transparent border-0 p-0"
                data-testid={`${TEST_ID_PREFIX}-close-button`}
              >
                <CloseIcon className="fill-dim-gray" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col px-6 gap-3 overflow-auto">
          <FormField
            className="mt-2 h-10"
            register={register}
            label="Group Name"
            placeHolder="Enter group name"
            type="text"
            name="name"
            rules={{ required: true }}
            autoCleanError={false}
            hasSpacingOnFocus={false}
            resetValue={() => {
              resetField("name");
            }}
            disabled={false}
          />
          <FormField
            register={register}
            label="Description"
            placeHolder="Describe the group's purpose and responsibilities"
            name="description"
            rules={{ required: true }}
            autoCleanError={false}
            hasSpacingOnFocus={false}
            disabled={false}
            isAreaInput
          />
          <Controller
            name="status"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              const selectedOption =
                GROUP_STATUS_OPTIONS.find((option) => option.value === value) ||
                null;
              return (
                <Dropdown
                  value={selectedOption}
                  name="groupStatus"
                  className="mt-1"
                  labelBorder="Status"
                  placeholder="Status"
                  data={GROUP_STATUS_OPTIONS}
                  disabled={false}
                  onSelect={(data) => {
                    onChange(data?.value);
                    setValue("status", data?.value as GroupStatus);
                  }}
                />
              );
            }}
          />
          <PermissionTable
            isSubmitting={isSubmitting}
            permissions={getUserGroupPermission}
          />
        </div>
        <FormFooterActions
          primaryLabel="Create"
          primaryDisabled={!formState.isValid}
          isSubmitting={isSubmitting}
          secondaryLabel="Cancel"
          onSecondaryButtonClicked={onClose}
          testId={TEST_ID_PREFIX}
        />
      </form>
    </FormProvider>
  );
};

export default AddGroupPanel;
